import { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { GearFill } from 'react-bootstrap-icons';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import { toast } from 'react-toastify';
import { centerSettingsClient } from '../../shared/clients';
import { generatedApiCall } from '../../shared/apiClient';
import { confirm } from '../../utils/confirm';
import { ConfirmDialogProps } from '../../utils/ConfirmDialog';
import type { CenterSettingsModel } from '../../shared/generated';

const NUMBER_FIELDS: Record<string, { label: string; min?: number; max?: number }> = {
    ServerGrpcPort: { label: 'gRPC порт сервера', min: 1, max: 65535 },
    ServerHttpPort: { label: 'HTTP порт сервера', min: 1, max: 65535 },
    ClientServicePort: { label: 'ClientService порт (ARM)', min: 1, max: 65535 },
    MaxBadDataCount: { label: 'Макс. кол-во плохих данных' },
    StateSavePeriodInSeconds: { label: 'Период записи state, сек', min: 1 },
    StateBackupPeriodInSeconds: { label: 'Период бэкапов state, сек', min: 1 },
    DeviceQueriesLimit: { label: 'Лимит запросов устройств', min: 1 },
    DOSRowsLimit: { label: 'DOS: лимит строк', min: 1 },
    DOSCounterLimit: { label: 'DOS: лимит счётчика', min: 1 },
    DOSPeriodIncrementLimit: { label: 'DOS: шаг инкремента', min: 1 },
    DOSPeriodDecrementLimit: { label: 'DOS: шаг декремента', min: 1 },
    ChargePerMonth: { label: 'Абонплата, мес' },
    MinWorkDaysInMonth: { label: 'Мин. рабочих дней в месяце' },
    CounterSaveRatioInSeconds: { label: 'Период сохранения счётчиков, сек', min: 1 },
    AutoRepairMaxRateInSeconds: { label: 'Автопочинка: макс. скорость, сек', min: 1 },
    WebPort: { label: 'Веб-порт (Kestrel)', min: 1, max: 65535 },
    MetricsPort: { label: 'Порт метрик (Prometheus)', min: 1, max: 65535 },
};

const BOOL_LABELS: Record<string, string> = {
    ArmHttpProtocolEnabled: 'Сервер ARM (ArmHttpProtocol)',
    ManageModelDeviceServices: 'Управлять службами ModelDeviceService',
    IsShowUserCommands: 'Показывать команды пользователей',
    IsShowRetransmission: 'Показывать ретрансляцию',
    RetransmissionEnabled: 'Включить сервис ретрансляции',
    GCCollectEnabled: 'Принудительная сборка GC',
    RepairDatabaseOnStart: 'Ремонт БД при старте',
    PurgingDatabaseOnStart: 'Очистка БД при старте',
    DatabasePurgingEnabled: 'Периодическая очистка БД',
    SendingDataInFutureDisabled: 'Запретить данные из будущего',
    StateBackupArchiveEnabled: 'Архивация бэкапов state',
    DeviceQueriesLimitEnabed: 'Лимит запросов устройств',
    DOSProtectedEnabled: 'DOS-защита',
    SuccessDataCounterDisabled: 'Отключить счётчик успешных данных',
    ShowDeviceError: 'Показывать ошибки устройств',
    ShowNaturalDeviceError: 'Показывать естественные ошибки устройств',
    ShowHardwareCommand: 'Показывать аппаратные команды',
    LogDeviceError: 'Логировать ошибки устройств',
    WriteLastPackageInState: 'Писать последний пакет в state',
    AutoRepairEnabled: 'Автопочинка данных',
    BadData_ConnectionCount: 'Подключения',
    BadData_SuccessCount: 'Успешные данные',
    BadData_DataInFuture: 'Данные из будущего',
    BadData_SpamerCount: 'Спамеры',
    BadData_UnknownError: 'Неизвестные ошибки',
    BadData_ReprogError: 'Ошибки перепрограммирования',
    BadData_Auxiliary: 'Служебные данные',
    BadData_PastDueRestPackage: 'Просроченные пакеты',
    BadData_GarbageBytes: 'Мусорные байты',
    BadData_NotValidPackageLength: 'Неверная длина пакета',
    BadData_ControlSumPackageFailed: 'Ошибка КС пакета',
    BadData_NotValidSecretCode: 'Неверный секретный код',
    BadData_NotValidDate: 'Неверная дата',
    BadData_UnknownPackageCommandType: 'Неизвестная команда пакета',
    BadData_ForeignerModel: 'Чужая модель',
    BadData_UnknownImei: 'Неизвестный IMEI',
    BadData_UnknownDevice: 'Неизвестное устройство',
    BadData_ControlSumDatumFailed: 'Ошибка КС данных',
    BadData_SoftFaultData: 'Сбойные данные',
    BadData_TruncatedDatum: 'Обрезанные данные',
    BadData_NotValidData: 'Невалидные данные',
    BadData_NotFoundClient: 'Клиент не найден',
    BadData_DeviceBanned: 'Заблокированное устройство',
    BadData_UnsupportedDataType: 'Неподдерживаемый тип данных',
    BadData_NotExistLastData: 'Нет предыдущих данных',
    BadData_DataInPastTime: 'Данные из прошлого',
    BadData_DataInFutureTime: 'Данные из будущего (время)',
    BadData_DuplicateDataByLastTime: 'Дубликат по последнему времени',
    BadData_DuplicateDataInDatabase: 'Дубликат в БД',
    BadData_UnknownDatabaseError: 'Неизвестная ошибка БД',
    EnableMetricServer: 'Сервер метрик (Prometheus)',
};

interface IGroup {
    title: string;
    fields: (keyof CenterSettingsModel)[];
}

const GROUPS: IGroup[] = [
    {
        title: 'Сервер и порты',
        fields: ['WebPort', 'ServerGrpcPort', 'ServerHttpPort', 'MetricsPort', 'EnableMetricServer',
            'ClientServicePort', 'ArmHttpProtocolEnabled', 'ArmHttpProtocolUri'],
    },
    {
        title: 'Поведение сервера',
        fields: ['ManageModelDeviceServices', 'IsShowUserCommands', 'IsShowRetransmission', 'RetransmissionEnabled', 'GCCollectEnabled',
            'RepairDatabaseOnStart', 'PurgingDatabaseOnStart', 'DatabasePurgingEnabled', 'MaxBadDataCount',
            'SendingDataInFutureDisabled'],
    },
    {
        title: 'State-бэкапы',
        fields: ['StateSavePeriodInSeconds', 'StateBackupPeriodInSeconds', 'StateBackupArchiveEnabled'],
    },
    {
        title: 'Ограничения',
        fields: ['DeviceQueriesLimitEnabed', 'DeviceQueriesLimit', 'DOSProtectedEnabled', 'DOSRowsLimit',
            'DOSCounterLimit', 'DOSPeriodIncrementLimit', 'DOSPeriodDecrementLimit'],
    },
    {
        title: 'Клиенты',
        fields: ['ChargePerMonth', 'MinWorkDaysInMonth'],
    },
    {
        title: 'Логирование',
        fields: ['SuccessDataCounterDisabled', 'ShowDeviceError', 'ShowNaturalDeviceError', 'ShowHardwareCommand',
            'LogDeviceError', 'WriteLastPackageInState', 'CounterSaveRatioInSeconds', 'AutoRepairEnabled',
            'AutoRepairMaxRateInSeconds'],
    },
    {
        title: 'Плохие данные (логирование)',
        fields: ['BadData_ConnectionCount', 'BadData_SuccessCount', 'BadData_DataInFuture', 'BadData_SpamerCount',
            'BadData_UnknownError', 'BadData_ReprogError', 'BadData_Auxiliary', 'BadData_PastDueRestPackage',
            'BadData_GarbageBytes', 'BadData_NotValidPackageLength', 'BadData_ControlSumPackageFailed',
            'BadData_NotValidSecretCode', 'BadData_NotValidDate', 'BadData_UnknownPackageCommandType',
            'BadData_ForeignerModel', 'BadData_UnknownImei', 'BadData_UnknownDevice',
            'BadData_ControlSumDatumFailed', 'BadData_SoftFaultData', 'BadData_TruncatedDatum',
            'BadData_NotValidData', 'BadData_NotFoundClient', 'BadData_DeviceBanned',
            'BadData_UnsupportedDataType', 'BadData_NotExistLastData', 'BadData_DataInPastTime',
            'BadData_DataInFutureTime', 'BadData_DuplicateDataByLastTime', 'BadData_DuplicateDataInDatabase',
            'BadData_UnknownDatabaseError'],
    },
];

interface CenterSettingsDialogProps {
    buttonVariant: 'custom' | 'transparent';
    buttonText?: string;
}

const CenterSettingsDialog = (props: CenterSettingsDialogProps) => {
    const { buttonVariant, buttonText } = props;
    const [open, setOpen] = useState(false);
    const [settings, setSettings] = useState<CenterSettingsModel | null>(null);
    const [savedSettings, setSavedSettings] = useState<CenterSettingsModel | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [tabIndex, setTabIndex] = useState(0);

    const fetchSettings = () => {
        setLoading(true);
        generatedApiCall(() => centerSettingsClient.getCenterSettings())
            .then((data) => {
                if (data) {
                    setSettings({ ...data });
                    setSavedSettings({ ...data });
                }
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (open) {
            fetchSettings();
        }
    }, [open]);

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        if (saving) return;
        setOpen(false);
        setSettings(null);
        setSavedSettings(null);
    };

    const isDirty = (): boolean => {
        if (!settings || !savedSettings) return false;
        return (Object.keys(settings) as (keyof CenterSettingsModel)[])
            .some((key) => settings[key] !== savedSettings[key]);
    };

    const handleChange = (field: keyof CenterSettingsModel, value: string | boolean) => {
        setSettings((prev) => {
            if (!prev) return prev;
            const meta = NUMBER_FIELDS[field as string];
            if (meta && typeof value === 'string') {
                const num = parseInt(value);
                return { ...prev, [field]: isNaN(num) ? 0 : num };
            }
            return { ...prev, [field]: value };
        });
    };

    const handleRestartNow = async () => {
        try {
            const data = await generatedApiCall(() => centerSettingsClient.restartCenterService());
            if (data?.Success) {
                toast.info(data.Message || 'Служба Center перезапускается...', { autoClose: 10000 });
                setOpen(false);
            } else {
                toast.error('Не удалось запланировать перезапуск службы Center.');
            }
        } catch {
            toast.error('Не удалось связаться с сервером для перезапуска.');
        }
    };

    const handleSave = async () => {
        if (!settings) return;
        setSaving(true);
        try {
            const data = await generatedApiCall(() => centerSettingsClient.updateCenterSettings(settings));
            if (!data) return;
            if (!data.Success) {
                toast.error(data.Message || 'Не удалось сохранить настройки.');
                return;
            }
            toast.success(data.Message || 'Настройки сохранены.');
            if (data.FirewallResult)
                toast.info(data.FirewallResult, { autoClose: 10000 });
            if (data.LinuxFirewallHint)
                toast.info(data.LinuxFirewallHint, { autoClose: 10000 });
            setSavedSettings({ ...settings });
            if (data.RestartRequired) {
                const ok = await confirm(new ConfirmDialogProps(
                    'Настройки сохранены. Изменения портов и ряда флагов применяются после перезапуска службы Center. Перезапустить сейчас?',
                    { body: 'Во время перезапуска веб-интерфейс и приём данных будут недоступны около минуты.' }));
                if (ok) {
                    await handleRestartNow();
                }
            }
        } finally {
            setSaving(false);
        }
    };

    const renderField = (field: keyof CenterSettingsModel) => {
        if (!settings) return null;
        const value = settings[field];
        // Поля клиентского сервиса видимы только при включённом ARM-протоколе:
        // при выключенном флаге служба не стартует, порт не проверяется
        // (CenterSettingsService.Validate) - значения скрытых полей сохраняются.
        if ((field === 'ClientServicePort' || field === 'ArmHttpProtocolUri') && !settings.ArmHttpProtocolEnabled) {
            return null;
        }
        if (typeof value === 'boolean') {
            return (
                <Col md={6} lg={4} key={field}>
                    <Form.Check
                        id={`centerSettings_${field}`}
                        type="checkbox"
                        label={BOOL_LABELS[field as string] ?? field}
                        checked={value}
                        onChange={(e) => handleChange(field, e.target.checked)}
                    />
                </Col>
            );
        }
        if (field === 'ArmHttpProtocolUri') {
            return (
                <Col md={6} lg={4} key={field}>
                    <Form.Group>
                        <Form.Label>URI сервера ARM</Form.Label>
                        <Form.Control
                            type="text"
                            value={String(value ?? '')}
                            onChange={(e) => handleChange(field, e.target.value)}
                            placeholder="http://localhost/ArmData/"
                        />
                    </Form.Group>
                </Col>
            );
        }
        const meta = NUMBER_FIELDS[field as string] ?? { label: field };
        return (
            <Col md={6} lg={4} key={field}>
                <Form.Group>
                    <Form.Label>{meta.label}</Form.Label>
                    <Form.Control
                        type="number"
                        min={meta.min}
                        max={meta.max}
                        value={String(value ?? '')}
                        onChange={(e) => handleChange(field, e.target.value)}
                    />
                </Form.Group>
            </Col>
        );
    };

    return (
        <>
            <Button variant={buttonVariant} onClick={handleOpen} title="Настройки Center.json">
                <GearFill className="svg" />
                {buttonText}
            </Button>
            <Modal show={open} onHide={handleClose} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Настройки Center.json</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loading && !settings ?
                        <div style={{ padding: '2rem', textAlign: 'center' }}>Загрузка...</div> :
                        settings && (
                            <Tabs forceRenderTabPanel={true} className="modes centerSettingsTabs"
                                selectedIndex={tabIndex} onSelect={setTabIndex}>
                                <TabList>
                                    {GROUPS.map((group) => (
                                        <Tab key={group.title} title={group.title}>
                                            <label>{group.title}</label>
                                        </Tab>
                                    ))}
                                </TabList>
                                {GROUPS.map((group, idx) => (
                                    <TabPanel key={group.title} tabIndex={idx} forceRender={true}>
                                        <Form style={{ padding: '1rem' }}>
                                            <Row>
                                                {group.fields.map((f) => renderField(f))}
                                            </Row>
                                        </Form>
                                    </TabPanel>
                                ))}
                            </Tabs>
                        )}
                </Modal.Body>
                <Modal.Footer>
                    {isDirty() && <span style={{ marginRight: 'auto', color: 'var(--warning)' }}>Есть несохранённые изменения</span>}
                    <Button variant="secondary" onClick={handleClose}>Закрыть</Button>
                    <Button variant="custom" disabled={saving || !isDirty()} onClick={handleSave}>Сохранить</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CenterSettingsDialog;